/**
 * React Query hook for Trust Account List V2 with RDS-based pagination
 * Uses the new listTrustAccountV2 API with improved filtering and sorting
 * Shared hook used by both TrustAccountingV2 and OperatingAccountingV2
 */

import { useQuery } from '@tanstack/react-query';
import { listTrustAccountV2 } from 'services/billingServices';
import { queryKeys } from '../../config/queryClient';

/**
 * Fetches trust account data with RDS-based pagination
 * API Response format: { total_count, transaction_detail: [], page_count, page, limit }
 *
 * @param {Object} params - Request parameters
 * @returns {Promise<Object>} - Response with data array and pagination metadata
 */
const fetchTrustAccountV2 = async ({
  limit = 10,
  page = 1,
  case_id = null,
  contact_id = null,
  account_id = null,
  account_type = null,
  transaction_date_lower = null,
  transaction_date_upper = null,
  source_account_id = [],
  destination_account_id = [],
  sort_by = '',
  sort_order = '',
}) => {
  try {
    const response = await listTrustAccountV2({
      limit,
      page,
      case_id,
      contact_id,
      account_id,
      account_type,
      transaction_date_lower,
      transaction_date_upper,
      source_account_id,
      destination_account_id,
      sort_by,
      sort_order,
    });

    if (response?.data) {
      return response.data;
    }

    // Return empty structure if no data (matching API format)
    return {
      total_count: 0,
      transaction_detail: [],
      page_count: 0,
      page: page,
      limit: limit,
    };
  } catch (error) {
    console.error('Error fetching trust account V2:', error);
    throw error;
  }
};

/**
 * Hook to fetch trust account transactions with RDS-based pagination using React Query
 *
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Items per page (default: 10)
 * @param {number} options.page - Page number (default: 1)
 * @param {string|null} options.case_id - Filter by case UUID
 * @param {string|null} options.contact_id - Filter by contact UUID
 * @param {string|null} options.account_id - Filter by account UUID
 * @param {string|null} options.account_type - Account type: 'trust' or 'operating' (default: null)
 * @param {string|null} options.transaction_date_lower - Start date filter (ISO format)
 * @param {string|null} options.transaction_date_upper - End date filter (ISO format)
 * @param {Array} options.source_account_id - Source account IDs (operating only)
 * @param {Array} options.destination_account_id - Destination account IDs (operating only)
 * @param {string} options.sort_by - Field to sort by (default: 'time')
 *   Allowed values: 'time', 'amount', 'case_name', 'contact_name', 'entry_type', 'payment_type', 'record_created_date'
 * @param {string} options.sort_order - Sort order: 'asc' or 'desc' (default: 'desc')
 * @param {boolean} options.enabled - Enable/disable query (default: true)
 *
 * @returns {Object} Query result object
 * @returns {Array} returns.transactions - Array of transaction objects
 * @returns {number} returns.totalRecords - Total number of records
 * @returns {number} returns.currentPage - Current page number
 * @returns {number} returns.totalPages - Total number of pages
 * @returns {number} returns.pageSize - Items per page
 * @returns {boolean} returns.isLoading - Initial loading state
 * @returns {boolean} returns.isFetching - Fetching state (including background refetch)
 * @returns {boolean} returns.isError - Error state indicator
 * @returns {Object|null} returns.error - Error object if request failed
 * @returns {Function} returns.refetch - Function to manually refetch data
 *
 * @example
 * const {
 *   transactions,
 *   totalRecords,
 *   totalPages,
 *   isLoading,
 *   refetch
 * } = useTrustAccountListV2Query({
 *   limit: 10,
 *   page: 1,
 *   case_id: 'case-uuid',
 *   account_id: 'account-uuid',
 *   account_type: 'trust',
 *   sort_by: 'time',
 *   sort_order: 'desc',
 * });
 */
export const useTrustAccountListV2Query = ({
  limit = 10,
  page = 1,
  case_id = null,
  contact_id = null,
  account_id = null,
  account_type = null,
  transaction_date_lower = null,
  transaction_date_upper = null,
  source_account_id = [],
  destination_account_id = [],
  sort_by = '',
  sort_order = '',
  enabled = true,
} = {}) => {
  // Create stable query key based on all filter parameters
  const queryKey = queryKeys.trustAccounting.listV2(
    {
      case_id,
      contact_id,
      account_id,
      account_type,
      transaction_date_lower,
      transaction_date_upper,
      source_account_id,
      destination_account_id,
      sort_by,
      sort_order,
    },
    page,
    limit
  );

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey,
    queryFn: () =>
      fetchTrustAccountV2({
        limit,
        page,
        case_id,
        contact_id,
        account_id,
        account_type,
        transaction_date_lower,
        transaction_date_upper,
        source_account_id,
        destination_account_id,
        sort_by,
        sort_order,
      }),
    enabled,
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes - serves from cache before that
    gcTime: 10 * 60 * 1000, // Keep in cache for 10 minutes (React Query v4 uses gcTime instead of cacheTime)
    keepPreviousData: true, // Keep previous data while fetching
    refetchOnWindowFocus: false, // Don't refetch on window focus
    refetchOnMount: enabled, // Only refetch on mount if query is enabled
    retry: (failureCount) => {
      // Retry twice for errors
      return failureCount < 2;
    },
  });

  return {
    transactions: data?.data || [],
    totalRecords: data?.total_count || 0,
    currentPage: data?.page || page,
    totalPages: data?.page_count || 0,
    pageSize: data?.limit || limit,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

export default useTrustAccountListV2Query;
